import {
    Trophy,
    FileText,
    LogOut,
    Star,
    Flame,
    Building2,
    Volume2,
    VolumeX,
    X,
    History,
    CalendarClock,
    CalendarDays,
    Home,
    MessageSquare,
    Gauge,
    Archive,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { useSettings } from '../contexts/SettingsContext'
import { useMemberships } from '../hooks/useMemberships'
import { usePontaj } from '../hooks/usePontaj'
import IconButton from './ui/IconButton'
import CompanySwitcher from './CompanySwitcher'
import LanguageSwitcher from './LanguageSwitcher'

function getInitials(name) {
    if (!name) return '?'
    return name.split(' ').map((word) => word[0]).join('').toUpperCase().slice(0, 2)
}

export default function Sidebar({
    onShowLeaderboard,
    showLeaderboard,
    onShowProcedures,
    showProcedures,
    onShowHistory,
    showHistory,
    onShowScheduler,
    showScheduler,
    onShowCalendar,
    showCalendar,
    onShowHome,
    showHome,
    onShowSuggestions,
    showSuggestions,
    onShowCockpit,
    showCockpit,
    onShowArchive,
    showArchive,
    onShowProfile,
    onShowCompany,
    onCloseMobile,
    dashboardV2Enabled = false,
}) {
    const { user, profile, isAdmin, signOut } = useAuth()
    const { soundsOn, toggleSounds } = useSettings()
    const { t } = useTranslation()
    // Pontaj is now READ-ONLY in Kanbify — Pontify (Anviz) is the single source
    // of truth. The clock_in/clock_out RPCs still exist server-side and are used
    // by the pontify-clock-in webhook. No manual UI controls here.
    const { isActive, elapsed } = usePontaj(user?.id)
    const activeCompanyId = profile?.active_company_id || profile?.company_id
    const { memberships } = useMemberships(user?.id)
    const activeMembership = memberships.find((membership) => membership.company_id === activeCompanyId) || null
    const activeRole = activeMembership?.role || profile?.role
    const activeDisplayName = profile?.role === 'admin' && activeMembership?.companies?.name
        ? activeMembership.companies.name
        : profile?.full_name

    const levelPct = () => {
        if (!profile) return 0
        const level = profile.level || 1
        const points = profile.points || 0
        const nextLevel = level * 100
        const previousLevel = (level - 1) * 100

        return Math.min(100, Math.round(((points - previousLevel) / (nextLevel - previousLevel)) * 100))
    }

    return (
        <aside className="sidebar" aria-label={t('sidebar.aria.mainNav')}>
            <div className="sidebar-logo">
                <div className="sidebar-logo-icon">K</div>
                <span className="sidebar-logo-text">Kanbify</span>
                {onCloseMobile ? (
                    <IconButton
                        icon={X}
                        label={t('sidebar.closeMenu')}
                        className="sidebar-mobile-close"
                        onClick={onCloseMobile}
                    />
                ) : null}
            </div>

            <CompanySwitcher
                activeCompanyId={profile?.active_company_id || profile?.company_id}
                onCompanyChanged={() => window.location.reload()}
            />

            <div className="sidebar-scroll">
                {profile?.company_id && (
                    <div className="sidebar-pontaj">
                        {isActive ? (
                            <div
                                className="sidebar-pontaj-active sidebar-pontaj-readonly"
                                title={t('sidebar.pontaj.syncedTitle')}
                            >
                                <div className="sidebar-pontaj-info">
                                    <span className="sidebar-pontaj-dot" />
                                    <span className="sidebar-pontaj-label">{t('sidebar.pontaj.active')}</span>
                                    <span className="sidebar-pontaj-time">{elapsed}</span>
                                </div>
                                <span className="sidebar-pontaj-source">{t('sidebar.pontaj.source')}</span>
                            </div>
                        ) : (
                            <div
                                className="sidebar-pontaj-inactive"
                                title={t('sidebar.pontaj.inactiveTitle')}
                            >
                                <span className="sidebar-pontaj-dot sidebar-pontaj-dot-off" />
                                <div>
                                    <div className="sidebar-pontaj-label">{t('sidebar.pontaj.inactive')}</div>
                                    <div className="sidebar-pontaj-hint">{t('sidebar.pontaj.hint')}</div>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <nav className="sidebar-nav">
                    {dashboardV2Enabled && onShowHome ? (
                        <button
                            type="button"
                            className={`sidebar-nav-item ${showHome ? 'active' : ''}`}
                            onClick={onShowHome}
                            aria-current={showHome ? 'page' : undefined}
                            title={t('sidebar.nav.homeTitle')}
                        >
                            <Home size={18} />
                            {t('nav.home')}
                        </button>
                    ) : null}

                    <button
                        type="button"
                        className={`sidebar-nav-item ${showLeaderboard ? 'active' : ''}`}
                        onClick={onShowLeaderboard}
                        aria-current={showLeaderboard ? 'page' : undefined}
                        title={t('sidebar.nav.leaderboardTitle')}
                    >
                        <Trophy size={18} />
                        {t('nav.leaderboard')}
                    </button>

                    {profile?.company_id && onShowProcedures ? (
                        <button
                            type="button"
                            className={`sidebar-nav-item ${showProcedures ? 'active' : ''}`}
                            onClick={onShowProcedures}
                            aria-current={showProcedures ? 'page' : undefined}
                            title={t('sidebar.nav.proceduresTitle')}
                        >
                            <FileText size={18} />
                            {t('nav.procedures')}
                        </button>
                    ) : null}

                    {profile?.company_id && onShowHistory ? (
                        <button
                            type="button"
                            className={`sidebar-nav-item ${showHistory ? 'active' : ''}`}
                            onClick={onShowHistory}
                            aria-current={showHistory ? 'page' : undefined}
                            title={t('sidebar.nav.historyTitle')}
                        >
                            <History size={18} />
                            {t('nav.history')}
                        </button>
                    ) : null}

                    {profile?.company_id && onShowScheduler ? (
                        <button
                            type="button"
                            className={`sidebar-nav-item ${showScheduler ? 'active' : ''}`}
                            onClick={onShowScheduler}
                            aria-current={showScheduler ? 'page' : undefined}
                            title={t('sidebar.nav.schedulerTitle')}
                        >
                            <CalendarClock size={18} />
                            {t('nav.scheduler')}
                        </button>
                    ) : null}

                    {profile?.company_id && onShowCalendar ? (
                        <button
                            type="button"
                            className={`sidebar-nav-item ${showCalendar ? 'active' : ''}`}
                            onClick={onShowCalendar}
                            aria-current={showCalendar ? 'page' : undefined}
                            title={t('sidebar.nav.calendarTitle')}
                        >
                            <CalendarDays size={18} />
                            {t('nav.calendar')}
                        </button>
                    ) : null}

                    {onShowSuggestions ? (
                        <button
                            type="button"
                            className={`sidebar-nav-item ${showSuggestions ? 'active' : ''}`}
                            onClick={onShowSuggestions}
                            aria-current={showSuggestions ? 'page' : undefined}
                            title={t('sidebar.nav.suggestionsTitle')}
                        >
                            <MessageSquare size={18} />
                            {t('nav.suggestions')}
                        </button>
                    ) : null}

                    {onShowCockpit && (isAdmin || profile?.role === 'manager') ? (
                        <button
                            type="button"
                            className={`sidebar-nav-item ${showCockpit ? 'active' : ''}`}
                            onClick={onShowCockpit}
                            aria-current={showCockpit ? 'page' : undefined}
                            title={t('sidebar.nav.cockpitTitle')}
                        >
                            <Gauge size={18} />
                            {t('nav.cockpit')}
                        </button>
                    ) : null}

                    {profile?.company_id && onShowArchive ? (
                        <button
                            type="button"
                            className={`sidebar-nav-item ${showArchive ? 'active' : ''}`}
                            onClick={onShowArchive}
                            aria-current={showArchive ? 'page' : undefined}
                            title={t('sidebar.nav.archiveTitle')}
                        >
                            <Archive size={18} />
                            {t('nav.archive')}
                        </button>
                    ) : null}

                    <button
                        type="button"
                        className="sidebar-nav-item"
                        onClick={toggleSounds}
                        aria-pressed={soundsOn}
                        title={soundsOn ? t('sidebar.sounds.onTitle') : t('sidebar.sounds.offTitle')}
                    >
                        {soundsOn ? <Volume2 size={18} /> : <VolumeX size={18} />}
                        {soundsOn ? t('nav.soundsOn') : t('nav.soundsOff')}
                    </button>

                    <LanguageSwitcher variant="nav" />

                    {onShowCompany && profile?.company_id ? (
                        <button type="button" className="sidebar-nav-item" onClick={onShowCompany}
                            title={isAdmin ? t('sidebar.company.adminTitle') : t('sidebar.company.employeeTitle')}
                        >
                            <Building2 size={18} />
                            {isAdmin ? t('nav.company') : t('nav.team')}
                        </button>
                    ) : null}
                </nav>

            </div>

            <div className="sidebar-user-section">
                {profile ? (
                    <>
                        <div className="sidebar-level-row">
                            <span className="sidebar-level-label">{t('sidebar.levelShort', { level: profile.level || 1 })}</span>
                            <div className="sidebar-level-bar">
                                <div className="sidebar-level-fill" style={{ width: `${levelPct()}%` }} />
                            </div>
                            <span className="sidebar-points">
                                <Star size={10} />
                                {profile.points || 0}
                            </span>
                        </div>
                        {(profile.streak_days || 0) > 1 ? (
                            <div className="sidebar-streak">
                                <Flame size={12} />
                                {t('sidebar.streak', { count: profile.streak_days })}
                            </div>
                        ) : null}
                    </>
                ) : (
                    <div className="sidebar-profile-warning">{t('sidebar.profileUnavailable')}</div>
                )}

                <div className="sidebar-user">
                    <button
                        type="button"
                        className="sidebar-user-trigger"
                        onClick={profile ? onShowProfile : undefined}
                    >
                        <div
                            className="sidebar-user-avatar"
                            style={{
                                background: profile
                                    ? `hsl(${(profile.full_name?.length || 0) * 37 % 360}, 50%, 40%)`
                                    : 'var(--accent)',
                            }}
                        >
                            {getInitials(activeDisplayName)}
                        </div>
                        <div className="sidebar-user-info">
                            <div className="sidebar-user-name">{activeDisplayName || t('sidebar.noProfile')}</div>
                            <div className="sidebar-user-status">
                                {profile ? t(activeRole === 'admin' ? 'company.roleAdmin' : activeRole === 'manager' ? 'company.roleManager' : 'company.roleEmployee') : t('sidebar.sessionActive')}
                            </div>
                        </div>
                    </button>

                    <IconButton icon={LogOut} label={t('common.logout')} className="sidebar-logout" onClick={signOut} />
                </div>
            </div>
        </aside>
    )
}
